// Import specifier resolution (ARCHITECTURE.md §6.7, step 3.5).
//
// Language analyzers emit `imports` / `re_exports` edges whose `to` field is the
// raw module specifier as written in source ("./utils/logger.js", "react"), not
// a graph node id. Graph nodes, however, are keyed by project-relative file
// paths ("src/utils/logger.ts"). This mismatch breaks BFS traversal in the
// impact / dependency / context layers because `edge.to` never matches a node id.
//
// This module normalizes relative specifiers to project-relative file paths
// *before* the AstGraph is assembled, mirroring TypeScript's module-resolution
// extension/index probing. Bare specifiers (npm packages) and unresolvable
// relatives are left untouched — they may legitimately point outside the project.
//
// Path math uses `path.posix`: scanned relative paths use forward slashes
// (tinyglobby), and graph node ids must remain forward-slashed across platforms.

import { posix } from "node:path";

import type { GraphEdge } from "../types.js";

/** Edge kinds whose `to` field is a module specifier resolvable to a file. */
const RESOLVABLE_KINDS = new Set<GraphEdge["kind"]>(["imports", "re_exports"]);

/**
 * Compiled-output → source extension candidates. When a specifier carries an
 * emitted extension (".js"), the on-disk source is typically the TS counterpart.
 */
const OUTPUT_EXT_VARIANTS: Record<string, string[]> = {
  ".js": [".ts", ".tsx"],
  ".jsx": [".tsx"],
  ".mjs": [".mts"],
  ".cjs": [".cts"],
};

/** Extensions appended to an extensionless specifier, in resolution order. */
const APPEND_EXTS = [".ts", ".tsx", ".js", ".jsx"] as const;

/** Index-file extensions tried for a directory specifier, in resolution order. */
const INDEX_EXTS = [".ts", ".tsx", ".js"] as const;

/**
 * Build the ordered list of candidate project-relative paths for a normalized,
 * project-relative specifier base (the result of joining it onto the importer's
 * directory). The first candidate present in the project wins.
 */
function candidatePaths(resolved: string): string[] {
  const candidates: string[] = [resolved];

  const ext = posix.extname(resolved);
  const variants = OUTPUT_EXT_VARIANTS[ext];
  if (variants) {
    // e.g. "./logger.js" → try "logger.ts", "logger.tsx".
    const stem = resolved.slice(0, -ext.length);
    for (const variant of variants) candidates.push(stem + variant);
    return candidates;
  }

  // No recognized emitted extension: treat `resolved` as an extensionless base.
  // Append source extensions, then probe directory index files.
  for (const e of APPEND_EXTS) candidates.push(resolved + e);
  for (const e of INDEX_EXTS) candidates.push(posix.join(resolved, "index" + e));

  return candidates;
}

export interface ResolveSpecifierOptions {
  /** Raw module specifier as emitted by the analyzer (e.g. "./utils/logger.js"). */
  specifier: string;
  /** Project-relative path of the importing file (e.g. "src/app.ts"). */
  fromFile: string;
  /** All project-relative file paths known to the scan. */
  projectFiles: Set<string>;
}

/**
 * Resolve a module specifier to a project-relative file path, or `null` when it
 * is a bare (npm) specifier or cannot be matched to a project file.
 *
 * Relative specifiers ("./", "../") are resolved against the importer's
 * directory, then probed through TS-style extension and index variants.
 */
export function resolveSpecifier(options: ResolveSpecifierOptions): string | null {
  const { specifier, fromFile, projectFiles } = options;

  // Already a project file path (e.g. previously resolved, or an absolute-ish ref).
  if (projectFiles.has(specifier)) return specifier;

  const isRelative =
    specifier.startsWith("./") || specifier.startsWith("../");
  if (!isRelative) return null; // bare specifier → npm package, not a project file.

  // Resolve against the importer's directory and normalize "."/".." segments.
  const resolved = posix.normalize(
    posix.join(posix.dirname(fromFile), specifier),
  );

  // A specifier escaping the project root cannot match any project file.
  if (resolved.startsWith("..")) return null;

  for (const candidate of candidatePaths(resolved)) {
    if (projectFiles.has(candidate)) return candidate;
  }
  return null;
}

export interface ResolveAllEdgesOptions {
  /** Edges to resolve (typically one file's analysis output). */
  edges: GraphEdge[];
  /** All project-relative file paths known to the scan. */
  projectFiles: Set<string>;
}

/**
 * Return a new edge array in which every resolvable import/re-export specifier
 * has been rewritten to its project-relative file path. Edges of other kinds
 * (type_reference, calls, …) and unresolvable specifiers are passed through
 * unchanged. The input array and its edges are never mutated.
 */
export function resolveAllEdges(options: ResolveAllEdgesOptions): GraphEdge[] {
  const { edges, projectFiles } = options;

  return edges.map((edge) => {
    if (!RESOLVABLE_KINDS.has(edge.kind)) return edge;

    const resolved = resolveSpecifier({
      specifier: edge.to,
      fromFile: edge.from,
      projectFiles,
    });
    if (resolved === null || resolved === edge.to) return edge;

    return { ...edge, to: resolved };
  });
}
